/** Порядок строк в списке игроков. */

import type { PlayerCombat, PlayerStats } from '@ak/shared';

/**
 * Строка базы игроков: анкета и боевые цифры по каптам.
 *
 * `combat` равен `null` у того, кто не был ни на одном капте. Именно `null`,
 * а не нули: «не выходил» и «выходил без урона» — разные вещи, и сортировка
 * обязана их различать.
 */
export interface PlayerRow extends PlayerStats {
  combat: PlayerCombat | null;
}

/** Поля анкеты, по которым сортируется список. */
type ProfileSortKey = keyof Pick<
  PlayerStats,
  'tier' | 'gameNick' | 'staticId' | 'mclVzzCount' | 'captureCount' | 'mainCount' | 'reserveCount' | 'updatedAt'
>;

/** Боевые поля: их у части строк нет вовсе, поэтому сравниваются иначе. */
type CombatSortKey = keyof Pick<
  PlayerCombat,
  'captures' | 'damage' | 'kills' | 'kd' | 'avgDamage' | 'avgKills' | 'wins' | 'capturesMonth' | 'lastCaptureAt'
>;

export type PlayerSortKey = ProfileSortKey | CombatSortKey;

/**
 * Все ключи сортировки — для разбора адресной строки.
 *
 * Записаны через `Record`, а не списком: у записи компилятор требует все
 * ключи, и забытый при добавлении новой колонки уронит сборку. Список тем и
 * плох, что молча остаётся неполным, а проявляется это уже ссылкой, которая
 * «почему-то сортирует не так».
 */
const SORT_KEY_SET: Record<PlayerSortKey, true> = {
  tier: true,
  gameNick: true,
  staticId: true,
  mclVzzCount: true,
  captureCount: true,
  mainCount: true,
  reserveCount: true,
  updatedAt: true,
  captures: true,
  damage: true,
  kills: true,
  kd: true,
  avgDamage: true,
  avgKills: true,
  wins: true,
  capturesMonth: true,
  lastCaptureAt: true,
};

export function isSortKey(value: string): value is PlayerSortKey {
  return Object.hasOwn(SORT_KEY_SET, value);
}

const COMBAT_KEYS: readonly PlayerSortKey[] = [
  'captures',
  'damage',
  'kills',
  'kd',
  'avgDamage',
  'avgKills',
  'wins',
  'capturesMonth',
  'lastCaptureAt',
];

/** Времена сравниваются как строки: ISO-8601 сам расставляет их по порядку. */
const TIMESTAMP_KEYS: readonly PlayerSortKey[] = ['updatedAt', 'lastCaptureAt'];

export function isCombatKey(key: PlayerSortKey): key is CombatSortKey {
  return COMBAT_KEYS.includes(key);
}

const collator = new Intl.Collator('ru', { numeric: true, sensitivity: 'base' });

function compareValues(left: string | number, right: string | number, key: PlayerSortKey): number {
  if (TIMESTAMP_KEYS.includes(key)) {
    return left < right ? -1 : left > right ? 1 : 0;
  }

  return typeof left === 'number' && typeof right === 'number'
    ? left - right
    : collator.compare(String(left), String(right));
}

/**
 * Сравнивает две строки списка.
 *
 * Тир сортируется по особому правилу. Ноль означает «тира нет»: по значению
 * такая строка встала бы впереди первого тира, а перевёрнутый порядок поднял
 * бы безтировых на самый верх. В обе стороны наверху оказывалось бы не то,
 * ради чего сортируют, поэтому они прижаты к низу независимо от направления.
 *
 * Тем же правилом живут боевые колонки: у кого каптов не было, тот внизу в
 * обе стороны. Иначе сортировка «по урону вверх» показывала бы первым делом
 * тех, кто вообще не воевал, — сорок пустых строк перед первой осмысленной.
 */
export function comparePlayers(
  left: PlayerRow,
  right: PlayerRow,
  sortKey: PlayerSortKey,
  ascending: boolean,
): number {
  const direction = ascending ? 1 : -1;

  if (sortKey === 'tier') {
    if ((left.tier === 0) !== (right.tier === 0)) {
      return left.tier === 0 ? 1 : -1;
    }

    const byTier = (left.tier - right.tier) * direction;
    return byTier === 0 ? collator.compare(left.gameNick, right.gameNick) : byTier;
  }

  if (isCombatKey(sortKey)) {
    if ((left.combat === null) !== (right.combat === null)) {
      return left.combat === null ? 1 : -1;
    }

    if (left.combat === null || right.combat === null) {
      return collator.compare(left.gameNick, right.gameNick);
    }

    // Пусто (K/D ещё не пришёл с сайта, нет даты капта) — в самый низ значений.
    const empty = sortKey === 'kd' ? -1 : '';
    const byCombat = compareValues(left.combat[sortKey] ?? empty, right.combat[sortKey] ?? empty, sortKey);
    return (byCombat === 0 ? collator.compare(left.gameNick, right.gameNick) : byCombat) * direction;
  }

  const result = compareValues(left[sortKey], right[sortKey], sortKey);

  // Равные значения разводятся по нику, иначе порядок таких строк менялся бы
  // от перерисовки к перерисовке и список «дрожал» бы на глазах.
  return (result === 0 ? collator.compare(left.gameNick, right.gameNick) : result) * direction;
}
